import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import EssayDropbox from '@/components/university/university/EssayDropbox';
import SEO from '@/components/university/common/SEO';
import { getDashboardPath } from '@/utils/getDashboardPath'

// ─── Design tokens ────────────────────────────────────────────────────────────
const UNI_MIDNIGHT = '#06060c';
const UNI_DARK = '#0a0a14';
const UNI_GOLD = '#d4a843';
const UNI_INDIGO = '#0ea5e9';
const UNI_BORDER = 'rgba(255,255,255,0.07)';
const UNI_CARD = 'rgba(255,255,255,0.04)';
const UNI_TEXT = 'rgba(255,255,255,0.88)';
const UNI_MUTED = 'rgba(255,255,255,0.45)';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

interface EnrolledDegree {
  class_id: string;
  name: string;
  category_id: string;
  module_plan: { module_id: number; title: string; lessons: { lesson_id: number }[] }[];
  lesson_count?: number | null;
  mastery_score: number;
  last_active_at: string | null;
  progress_state: {
    current_unit: number;
    current_lesson: number;
    total_lessons_completed: number;
  } | null;
}

interface Certificate {
  id: string;
  cert_type: 'module' | 'degree' | 'honours';
  degree_slug: string;
  module_slug: string | null;
  issued_at: string;
}

interface EnrolledPrep {
  prep_slug: string;
  status: 'in_progress' | 'completed';
  last_active_at: string | null;
  title?: string;
  icon?: string;
  class_id?: string;
}

function getMasteryColor(score: number) {
  if (score >= 80) return '#4ade80';
  if (score >= 60) return UNI_GOLD;
  return '#f87171';
}

function Loader() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: UNI_MIDNIGHT, flexDirection: 'column', gap: 20,
    }}>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
        style={{
          width: 44, height: 44, borderRadius: '50%',
          border: `3px solid rgba(212,168,67,0.15)`, borderTopColor: UNI_GOLD,
        }}
      />
      <p style={{ color: UNI_MUTED, fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
        Loading your university…
      </p>
    </div>
  );
}

import PerformanceInsight from '@/components/university/university/PerformanceInsight';
import ResumeLessonCard from '@/components/university/university/ResumeLessonCard';
import { CourseSearch } from '@/components/university/university/CourseSearch';
import { RecentActivity } from '@/components/university/university/RecentActivity';
import LanguagePicker from '@/components/university/ui/LanguagePicker'

export default function UniversityDashboardPage() {
  const { user, userProfile, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [degrees, setDegrees] = useState<EnrolledDegree[]>([]);
  const [prepCourses, setPrepCourses] = useState<EnrolledPrep[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState<'degrees' | 'prep' | 'discover'>('degrees');
  const [facilityType, setFacilityType] = useState<string | null>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 820);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate('/signin', { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      try {
        // Load enrolled classes that belong to the university facility
        const { data: enrolments } = await supabase
          .from('class_enrollments')
          .select(`
            class_id, mastery_score, last_active_at, progress_state,
            classes (id, name, category_id, module_plan, facility_id, curriculum_track, lesson_count)
          `)
          .eq('student_id', user!.id)
          .order('last_active_at', { ascending: false });

        if (userProfile?.facility_id && userProfile.facility_role === 'admin') {
          const { data: fac } = await supabase
            .from('facilities')
            .select('type')
            .eq('id', userProfile.facility_id)
            .single();
          if (fac) setFacilityType(fac.type);
        }

        if (cancelled) return;

        const uniEnrolments = (enrolments || []).filter(
          (e) => (e.classes as any)?.facility_id === '00000000-0000-0000-0000-000000000002'
            && (e.classes as any)?.curriculum_track !== 'faith'   // faith 9 belong to the church surface
        );

        const mappedDegrees: EnrolledDegree[] = uniEnrolments.map((e) => {
          const cls = e.classes as any;
          return {
            class_id: e.class_id,
            name: cls?.name || 'Untitled',
            category_id: cls?.category_id || '',
            module_plan: cls?.module_plan || [],
            lesson_count: cls?.lesson_count ? Number(cls.lesson_count) : null,
            mastery_score: Number(e.mastery_score) || 0,
            last_active_at: e.last_active_at,
            progress_state: e.progress_state as EnrolledDegree['progress_state'],
          };
        });

        setDegrees(mappedDegrees);

        // Load prep enrolments
        const { data: preps } = await supabase
          .from('prep_enrolments')
          .select('prep_slug, status, last_active_at')
          .eq('user_id', user!.id)
          .order('last_active_at', { ascending: false });

        if (cancelled) return;

        // Map prep slugs to data (copying key attributes from PrepPage logic)
        const prepMeta: Record<string, { title: string, icon: string, class_id: string }> = {
          'prep-critical-thinking': { title: t('university.prep.programmes.prep-critical-thinking.label', 'Critical Thinking'), icon: '🔍', class_id: '72b58fdd-0847-4263-8984-77cb72b1fc95' },
          'prep-study-skills': { title: t('university.prep.programmes.prep-study-skills.label', 'Study Skills'), icon: '📚', class_id: 'c2d365e1-cd5d-4417-9986-182dcc089618' },
          'prep-career-exploration': { title: t('university.prep.programmes.prep-career-exploration.label', 'Career'), icon: '🎯', class_id: '7bb810fa-0c92-424a-91c0-7a6a048effad' },
          'prep-personal-finance': { title: t('university.prep.programmes.prep-personal-finance.label', 'Finance'), icon: '💳', class_id: '82e428c4-f80c-4474-a930-5e3eff6ccb59' },
          'prep-philosophy-ethics': { title: t('university.prep.programmes.prep-philosophy-ethics.label', 'Philosophy'), icon: '🤔', class_id: '792af34b-064b-4760-b0cd-e0b4270315e7' },
          'prep-psychology': { title: t('university.prep.programmes.prep-psychology.label', 'Psychology'), icon: '🧠', class_id: 'c9045fe0-5070-481f-a6dd-5c154e414124' },
          'prep-sociology': { title: t('university.prep.programmes.prep-sociology.label', 'Sociology'), icon: '👥', class_id: '1fc54151-bcdd-413e-980c-3fc182ceff79' },
          'prep-statistics': { title: t('university.prep.programmes.prep-statistics.label', 'Statistics'), icon: '📊', class_id: '1413c9e2-15be-41c6-bc89-a9378aab2798' },
          'prep-entrepreneurship': { title: t('university.prep.programmes.prep-entrepreneurship.label', 'Business'), icon: '🚀', class_id: 'e4d7dd24-c6f4-48c2-ba02-16e22aea3c7a' },
          'prep-calculus': { title: t('university.prep.programmes.prep-calculus.label', 'Calculus'), icon: '∫', class_id: '56caf32d-c584-447e-8e67-04c52ae6076d' },
        };

        const mappedPreps: EnrolledPrep[] = (preps || []).map(p => ({
          ...p,
          title: prepMeta[p.prep_slug]?.title || p.prep_slug,
          icon: prepMeta[p.prep_slug]?.icon || '🎓',
          class_id: prepMeta[p.prep_slug]?.class_id,
        }) as EnrolledPrep);

        setPrepCourses(mappedPreps);

        // Load certificates
        const { data: certs } = await supabase
          .from('university_certificates')
          .select('id, cert_type, degree_slug, module_slug, issued_at')
          .eq('user_id', user!.id)
          .order('issued_at', { ascending: false });

        if (cancelled) return;
        setCertificates((certs || []) as Certificate[]);

      } catch (err) {
        console.error('UniversityDashboard loadData error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [user]);

  if (authLoading || loading) return <Loader />;

  const totalLessons = degrees.reduce(
    (s, d) => s + (d.progress_state?.total_lessons_completed || 0), 0
  );
  const avgMastery = degrees.length
    ? Math.round(degrees.reduce((s, d) => s + d.mastery_score, 0) / degrees.length)
    : 0;

  // Find most recent degree to resume
  const lastActiveDegree = degrees.length > 0 ? degrees[0] : null;
  const resumeData = lastActiveDegree ? {
    class_id: lastActiveDegree.class_id,
    name: lastActiveDegree.name,
    current_unit: lastActiveDegree.progress_state?.current_unit || 1,
    current_lesson: lastActiveDegree.progress_state?.current_lesson || 1
  } : null;

  const TX = 'rgba(255,255,255,0.88)'; // Fix for missing TX variable

  return (
    <div style={{
      background: UNI_MIDNIGHT, minHeight: '100vh',
      color: TX, fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      <SEO title="Dashboard" noindex />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '24px 16px 80px' : '48px 24px 80px' }}>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            marginBottom: 36,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'flex-end',
            gap: 20
          }}
        >
          <div>
            <p style={{ fontSize: 11, color: UNI_GOLD, letterSpacing: '2px', fontWeight: 700, marginBottom: 6 }}>
              {t('university.dashboard.eyebrow', 'STILL WATERS UNIVERSITY')}
            </p>
            <h1 style={{ fontSize: isMobile ? 24 : 32, fontWeight: 800, margin: 0, letterSpacing: '-0.5px', color: '#fff' }}>
              {t('university.dashboard.title', 'Your Learning Portal')}
            </h1>
          </div>
          <div style={{
            display: 'flex',
            gap: 10,
            width: isMobile ? '100%' : 'auto',
            overflowX: isMobile ? 'auto' : 'visible',
            paddingBottom: isMobile ? 4 : 0,
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
          }}>
            <LanguagePicker />
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/university/progress')}
              style={{
                background: UNI_CARD, border: `1px solid ${UNI_BORDER}`,
                borderRadius: 12, padding: '10px 18px', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, color: TX,
                whiteSpace: 'nowrap',
                flex: isMobile ? 1 : 'none'
              }}
            >
              📊 {t('university.nav.progress', 'Progress')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/university/certificates')}
              style={{
                background: UNI_CARD, border: `1px solid ${UNI_BORDER}`,
                borderRadius: 12, padding: '10px 18px', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, color: TX,
                whiteSpace: 'nowrap',
                flex: isMobile ? 1 : 'none'
              }}
            >
              🏆 {t('university.nav.certificates', 'Certs')} {certificates.length > 0 && `(${certificates.length})`}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03, boxShadow: '0 8px 24px rgba(212,168,67,0.3)' }}
              onClick={() => navigate('/university/enrol')}
              style={{
                background: `linear-gradient(135deg, ${UNI_GOLD} 0%, #b8912e 100%)`,
                color: '#06060c', border: 'none',
                borderRadius: 12, padding: '10px 20px', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                whiteSpace: 'nowrap',
                flex: isMobile ? 1 : 'none'
              }}
            >
              + {t('university.dashboard.addDegree', 'Add Degree')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => navigate('/settings')}
              style={{
                background: UNI_CARD, border: `1px solid ${UNI_BORDER}`,
                borderRadius: 12, padding: '10px 18px', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, color: TX,
                whiteSpace: 'nowrap',
                flex: isMobile ? 1 : 'none'
              }}
            >
              ⚙️ {t('university.nav.settings', 'Settings')}
            </motion.button>
            {userProfile?.facility_role === 'admin' && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                onClick={() => navigate(facilityType === 'university_ngo' ? '/university/ngo/dashboard' : '/university/corporate/admin')}
                style={{
                  background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.3)',
                  borderRadius: 12, padding: '10px 18px', cursor: 'pointer',
                  fontSize: 13, fontWeight: 700, color: '#0ea5e9',
                  whiteSpace: 'nowrap',
                  flex: isMobile ? 1 : 'none'
                }}
              >
                🏢 {facilityType === 'university_ngo' ? 'Impact Admin' : 'Corp Admin'}
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Course Search */}
        <div style={{ marginBottom: 32 }}>
          <CourseSearch />
        </div>

        {/* Resume Last Lesson */}
        <ResumeLessonCard lastDegree={resumeData} />

        {/* AI Performance Insight */}
        <PerformanceInsight />

        {/* Stats Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: isMobile ? 12 : 20,
          marginBottom: 40
        }}>
          {[
            { icon: '🎓', label: t('university.dashboard.statDegrees', 'Degrees'), value: degrees.length, color: UNI_INDIGO },
            { icon: '✅', label: t('university.dashboard.statLessons', 'Lessons'), value: totalLessons, color: '#4ade80' },
            { icon: '🎯', label: t('university.dashboard.statMastery', 'Mastery'), value: `${avgMastery}%`, color: UNI_GOLD },
            { icon: '🏆', label: t('university.dashboard.statCerts', 'Certs'), value: certificates.length, color: '#0ea5e9' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              whileHover={isMobile ? {} : { y: -3 }}
              style={{
                background: UNI_CARD, border: `1px solid ${UNI_BORDER}`,
                borderRadius: 20, padding: isMobile ? '16px' : '24px', transition: 'all 0.2s',
                boxShadow: isMobile ? 'none' : '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ fontSize: isMobile ? 20 : 24, marginBottom: 8 }}>{stat.icon}</div>
              <div style={{
                fontSize: isMobile ? 24 : 32, fontWeight: 800, letterSpacing: '-1px',
                color: stat.color, fontFamily: "'Inter', sans-serif",
                lineHeight: 1
              }}>
                {stat.value}
              </div>
              <div style={{ fontSize: 11, color: UNI_MUTED, marginTop: 6, fontWeight: 600, letterSpacing: '0.5px' }}>{stat.label.toUpperCase()}</div>
            </motion.div>
          ))}
        </div>
        {/* Recent Activity Log */}
        <RecentActivity />

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 6, marginBottom: 32,
          background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: 6,
          border: `1px solid ${UNI_BORDER}`, width: isMobile ? '100%' : 'fit-content',
        }}>
          {(() => {
            const tabs: ('degrees' | 'prep' | 'discover')[] = ['degrees'];
            if (userProfile?.prep_pathway_mode !== 'direct') tabs.push('prep');
            tabs.push('discover');

            return tabs.map((tab) => (
              <motion.button
                key={tab}
                whileHover={{ scale: 1.02 }}
                onClick={() => {
                  setActiveTab(tab);
                  // If they select prep but are bridge_only, we might want to hint that foundation is hidden
                }}
                style={{
                  background: activeTab === tab ? UNI_GOLD : 'transparent',
                  color: activeTab === tab ? '#06060c' : UNI_MUTED,
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: 10,
                  cursor: 'pointer', fontSize: 13, fontWeight: 700,
                  transition: 'all 0.15s',
                  flex: isMobile ? 1 : 'none'
                }}
              >
                {tab === 'degrees'
                  ? t('university.dashboard.tabDegrees', 'My Degrees')
                  : tab === 'prep'
                    ? t('university.dashboard.tabPrep', 'Prep Courses')
                    : t('university.dashboard.tabDiscover', 'Discover')}
              </motion.button>
            ));
          })()}
        </div>

        <AnimatePresence mode="wait">

          {/* MY DEGREES TAB */}
          {activeTab === 'degrees' && (
            <motion.div
              key="degrees"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              {degrees.length === 0 ? (
                <div style={{
                  background: UNI_CARD, border: `1px solid ${UNI_BORDER}`,
                  borderRadius: 20, padding: '80px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 52, marginBottom: 20 }}>🎓</div>
                  <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 10 }}>
                    {t('university.dashboard.noDegrees', 'No degrees enrolled yet')}
                  </h3>
                  <p style={{ color: UNI_MUTED, fontSize: 15, marginBottom: 32, maxWidth: 400, margin: '0 auto 32px' }}>
                    {t('university.dashboard.noDegreesHint', 'Choose from 45 university-level programmes across 10 colleges.')}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.04 }}
                    onClick={() => navigate('/university/enrol')}
                    style={{
                      background: `linear-gradient(135deg, ${UNI_GOLD}, #b8912e)`,
                      color: '#06060c', border: 'none', borderRadius: 12,
                      padding: '14px 36px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    {t('university.dashboard.browseDegrees', 'Browse Degrees')} →
                  </motion.button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
                  {degrees.map((degree, i) => {
                    const totalLessonsInDegree = Number(degree.lesson_count) || degree.module_plan.reduce(
                      (s, m) => s + (m.lessons?.length || 0), 0
                    );
                    const completedLessons = degree.progress_state?.total_lessons_completed || 0;
                    const progressPct = totalLessonsInDegree > 0
                      ? Math.min(Math.round((completedLessons / totalLessonsInDegree) * 100), 100)
                      : 0;
                    const currentModule = degree.module_plan.find(
                      m => m.module_id === (degree.progress_state?.current_unit || 1)
                    );

                    return (
                      <motion.div
                        key={degree.class_id}
                        custom={i}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{ y: -4, borderColor: 'rgba(212,168,67,0.4)' }}
                        style={{
                          background: UNI_CARD, border: `1px solid ${UNI_BORDER}`,
                          borderRadius: 20, padding: '28px',
                          cursor: 'pointer', transition: 'all 0.2s',
                        }}
                        onClick={() => navigate(`/university/degree/${degree.class_id}`)}
                      >
                        {/* Degree name */}
                        <h3 style={{
                          fontWeight: 700, fontSize: 17, color: '#fff',
                          margin: '0 0 8px', lineHeight: 1.3,
                        }}>
                          {degree.name.replace('BSc ', '')}
                        </h3>

                        {/* Current position */}
                        <p style={{ color: UNI_MUTED, fontSize: 13, margin: '0 0 20px' }}>
                          {currentModule
                            ? `${currentModule.title} · Lesson ${degree.progress_state?.current_lesson || 1}`
                            : t('university.dashboard.notStarted', 'Not started')}
                        </p>

                        {/* Progress bar */}
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontSize: 12, color: UNI_MUTED }}>
                              {completedLessons}/{totalLessonsInDegree} {t('university.dashboard.lessons', 'lessons')}
                            </span>
                            <span style={{ fontSize: 12, color: UNI_MUTED }}>{progressPct}%</span>
                          </div>
                          <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 100, height: 6, overflow: 'hidden' }}>
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${progressPct}%` }}
                              transition={{ duration: 0.9, ease: 'easeOut', delay: i * 0.05 }}
                              style={{
                                height: '100%', borderRadius: 100,
                                background: `linear-gradient(90deg, ${UNI_INDIGO}, ${UNI_GOLD})`,
                              }}
                            />
                          </div>
                        </div>

                        {/* Essay dropbox — shows only if an essay assignment exists for current module */}
                        {degree.progress_state?.current_unit && (
                          <div style={{ marginBottom: 16 }}>
                            <EssayDropbox
                              classId={degree.class_id}
                              moduleId={degree.progress_state.current_unit}
                              className={degree.name}
                            />
                          </div>
                        )}

                        {/* Footer */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            fontSize: 13, fontWeight: 700,
                            color: getMasteryColor(degree.mastery_score),
                          }}>
                            🎯 {degree.mastery_score}% {t('university.dashboard.mastery', 'mastery')}
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.05, background: 'rgba(212,168,67,0.25)' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/university/learn/${degree.class_id}`);
                            }}
                            style={{
                              background: 'rgba(212,168,67,0.12)', color: UNI_GOLD,
                              border: `1px solid rgba(212,168,67,0.3)`,
                              borderRadius: 8, padding: '7px 16px',
                              fontSize: 13, fontWeight: 600, cursor: 'pointer',
                              transition: 'background 0.15s',
                            }}
                          >
                            {completedLessons === 0
                              ? t('university.dashboard.startLesson', 'Start →')
                              : t('university.dashboard.continueLesson', 'Continue →')}
                          </motion.button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {/* PREP COURSES TAB */}
          {activeTab === 'prep' && (
            <motion.div
              key="prep"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              {prepCourses.length === 0 ? (
                <div style={{
                  background: UNI_CARD, border: `1px solid ${UNI_BORDER}`,
                  borderRadius: 20, padding: '80px 24px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 52, marginBottom: 20 }}>🔍</div>
                  <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 10 }}>
                    {t('university.dashboard.noPrep', 'No prep courses started')}
                  </h3>
                  <p style={{ color: UNI_MUTED, fontSize: 15, marginBottom: 32, maxWidth: 420, margin: '0 auto 32px' }}>
                    {t('university.dashboard.noPrepHint', 'Start your journey with free foundation subjects like Critical Thinking.')}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.04 }}
                    onClick={() => navigate('/prep')}
                    style={{
                      background: `linear-gradient(135deg, ${UNI_GOLD}, #b8912e)`,
                      color: '#06060c', border: 'none', borderRadius: 12,
                      padding: '14px 36px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    {t('university.dashboard.startPrep', 'Start Free Prep')} →
                  </motion.button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
                  {prepCourses.map((prep, i) => (
                    <motion.div
                      key={prep.prep_slug}
                      custom={i}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover={{ y: -4, borderColor: 'rgba(16,185,129,0.3)' }}
                      style={{
                        background: UNI_CARD, border: `1px solid ${UNI_BORDER}`,
                        borderRadius: 20, padding: '24px',
                        cursor: 'pointer', transition: 'all 0.2s',
                      }}
                      onClick={() => navigate(prep.class_id ? `/university/learn/${prep.class_id}` : '/prep')}
                    >
                      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 20 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                          {prep.icon}
                        </div>
                        <div>
                          <h3 style={{ fontWeight: 700, fontSize: 17, color: '#fff', margin: '0 0 4px' }}>
                            {prep.title}
                          </h3>
                          <span style={{ fontSize: 11, fontWeight: 800, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: 100 }}>
                            {prep.status === 'completed' ? t('university.prep.statusComplete', 'COMPLETED') : t('university.prep.statusInProgress', 'IN PROGRESS')}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: UNI_MUTED }}>
                          {prep.last_active_at ? `${t('university.dashboard.lastActive', 'Last active')}: ${new Date(prep.last_active_at).toLocaleDateString()}` : ''}
                        </span>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          style={{
                            background: 'rgba(16,185,129,0.1)', color: '#10b981',
                            border: '1px solid rgba(16,185,129,0.3)',
                            borderRadius: 8, padding: '6px 14px',
                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          }}
                        >
                          {prep.status === 'completed' ? t('university.prep.reviewBtn', 'Review') : t('university.prep.continueBtn', 'Continue')} →
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* DISCOVER TAB */}
          {activeTab === 'discover' && (
            <motion.div
              key="discover"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              style={{
                background: UNI_CARD, border: `1px solid ${UNI_BORDER}`,
                borderRadius: 20, padding: '48px', textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 48, marginBottom: 20 }}>🌐</div>
              <h3 style={{ fontWeight: 700, fontSize: 22, marginBottom: 12 }}>
                {t('university.dashboard.discoverTitle', '44 degrees awaiting you')}
              </h3>
              <p style={{ color: UNI_MUTED, fontSize: 15, maxWidth: 480, margin: '0 auto 32px', lineHeight: 1.7 }}>
                {t('university.dashboard.discoverSub', 'Technology, Business, Health, Arts, Science, Humanities, Languages, Education, Faith, and Personal Mastery.')}
              </p>
              <motion.button
                whileHover={{ scale: 1.04 }}
                onClick={() => navigate('/university/enrol')}
                style={{
                  background: `linear-gradient(135deg, ${UNI_GOLD}, #b8912e)`,
                  color: '#06060c', border: 'none', borderRadius: 12,
                  padding: '14px 36px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                }}
              >
                {t('university.dashboard.browseAll', 'Browse All Degrees')} →
              </motion.button>
            </motion.div>
          )}

        </AnimatePresence>

        {/* Recent certificates strip */}
        {certificates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            style={{ marginTop: 40 }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 16,
            }}>
              <h2 style={{ fontWeight: 700, fontSize: 18, margin: 0 }}>
                🏆 {t('university.dashboard.recentCerts', 'Recent Certificates')}
              </h2>
              <button
                onClick={() => navigate('/university/certificates')}
                style={{
                  background: 'none', border: 'none', color: UNI_GOLD,
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                {t('university.dashboard.viewAll', 'View all →')}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8 }}>
              {certificates.slice(0, 4).map((cert) => (
                <div
                  key={cert.id}
                  style={{
                    background: 'rgba(212,168,67,0.06)',
                    border: '1px solid rgba(212,168,67,0.2)',
                    borderRadius: 14, padding: '16px 20px',
                    minWidth: 220, flexShrink: 0,
                  }}
                >
                  <div style={{ fontSize: 22, marginBottom: 8 }}>
                    {cert.cert_type === 'degree' ? '🎓' : cert.cert_type === 'honours' ? '⭐' : '📜'}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: UNI_GOLD, marginBottom: 4 }}>
                    {cert.cert_type === 'degree'
                      ? t('university.certs.degree', 'Degree Certificate')
                      : cert.cert_type === 'honours'
                        ? t('university.certs.honours', 'University Honours')
                        : t('university.certs.module', 'Module Certificate')}
                  </div>
                  <div style={{ fontSize: 11, color: UNI_MUTED }}>
                    {new Date(cert.issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

      </div>
    </div>
  );
}
